/**
 * Payout-readiness for the Owners list "Payouts ready" stat (COL-461; the stat
 * counts every month's ready payouts since COL-515, this module computes them
 * one month at a time).
 *
 * A payout = CoLiviq paying an owner their rent net of the management fee,
 * for company_collects apartments only (COL-225: an owner_collects tenant
 * pays the owner directly, so CoLiviq never holds that money and there is
 * nothing to pay out — those apartments never join a group and never block
 * one). One payout per (property, effective lessor) group — the same
 * grouping key `generate_management_invoices` bills by
 * (20260911120000_management_invoices_consolidate_per_property.sql), applied
 * to this owner's company_collects apartments in that property.
 *
 * Ready = every tenancy that OWED RENT FOR THIS MONTH — including one that
 * ended mid-month, see owedRentForMonth — has that month's rent fully
 * collected (decided per tenancy, never on the group's summed totals — see
 * computeOwnerPayoutGroups) — reusing the exact expected/paid rules
 * `usePropertyRentPaidStats` uses (`expectedRentCentsForMonth`,
 * `isPaymentEffectivelyPaid`) — AND the group hasn't already been paid out:
 * checked against the settlement='offset' `management_invoices` row for that
 * (property, lessor, month), which is the existing record of CoLiviq
 * executing the netted payout. No invoice row yet is not a blocker — the
 * admin generates/marks it as part of acting on "ready".
 *
 * A group only counts when an invoice CAN record its payout: one whose every
 * unit bills a zero fee is dropped by the generator before insert, so nothing
 * could ever settle it (`billable` below). A live portfolio-wide consolidated
 * invoice counts for every property it covers, because while one exists the
 * generator bills none of them.
 *
 * COL-474 adds the payouts VIEW on top of the same groups: a status per group
 * (completed / ready / pending), how much rent and how many rooms are still
 * missing, and the LIVE payout — rent collected minus the live, collected-based
 * fee. That fee is the one the offset invoice itself now carries: the DB keeps
 * an unsent offset invoice's amount in step with collected rent
 * (20260925110000_offset_invoice_live_fee.sql), so when a live invoice exists
 * its amount IS the fee and the view reads it straight off the row. The
 * estimate below only fills in while no invoice exists yet, and mirrors that
 * migration's per-model rule — see liveUnitFeeCents.
 */
import {
  RENT_COLLECTION_STATUSES,
  isNoticePeriodStatus,
} from "@/lib/rent-collection";
import {
  getFullMonthlyRentFromBooking,
  isPaymentEffectivelyPaid,
  resolveRentSettlement,
  latestProofByPaymentId,
} from "@/lib/rent";
import { expectedRentCentsForMonth } from "@/lib/rent-room-sync";
import { kickbackCents, ownerRentCents } from "@/lib/rent-kickback";
import { unitMonthlyRevenue, type UnitFeeConfig } from "@/lib/owner-metrics";
import {
  effectiveStartDate,
  isBookingArchived,
} from "@/hooks/v2/useProperties";
import type { CollectionMode } from "@/types/property";

export interface PayoutApartmentInput {
  id: number | string;
  propertyId: number | string;
  /** Apartment override ?? property default, mirroring resolveEffectiveRevenue's
   *  collection_mode rule (useProperties.ts:1088-1091). */
  collectionMode: CollectionMode;
  /** EFFECTIVE (apartment → property) fee config, resolved by the caller with
   *  resolveUnitFeeConfig. Decides whether this unit can bill a fee at all —
   *  see `billable` on OwnerPayoutGroup. */
  feeConfig: UnitFeeConfig;
  /** Display only (COL-617): the apartment's label in the payout popup. */
  label?: string | null;
}

export interface PayoutRoomRow {
  id: number | string;
  apartmentId: number | string | null;
  /** Display only (COL-617): the room's name in the payout popup. */
  title?: string | null;
  price: number | null;
  /** Mirrors generate_management_invoices's own occupancy test (rm.status =
   *  'occupied' OR rm.available = false) — used ONLY to decide whether this
   *  room is fee-bearing (billable below), never to decide which tenancy's
   *  rent is expected/paid (that stays booking-driven, via owedRentForMonth).
   *  The two signals can disagree — these
   *  flags aren't reliably maintained against booking dates — and using the
   *  booking-only signal for billability let a unit the generator will
   *  actually bill still read as unbillable here. */
  status?: string | null;
  available?: boolean | null;
}

export interface PayoutBookingRow {
  id: number | string;
  roomId: number | string | null;
  /** Display only (COL-617): the tenant's name in the payout popup. */
  name?: string | null;
  monthly_rent: number | null;
  first_month_rent_override: number | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  admin_archived_at: string | null;
  actual_move_in_date: string | null;
  move_in_date: string | null;
  actual_move_out_date: string | null;
  move_out_date: string | null;
  rent_discount_mode: string | null;
  rent_discount_value: number | null;
  rent_discount_months: number | null;
  rent_discount_start_month: string | null;
}

export interface PayoutPaymentRow {
  id: string;
  bookingId: number | string | null;
  amount_cents: number | null;
  /** COL-621: the full rent when a coliviq-funded kickback or referral credit
   *  lowered `amount_cents`. Payouts count COALESCE(gross, amount) — what the
   *  owner earned (src/lib/rent-kickback.ts). Optional: a caller that does not
   *  select it counts `amount_cents`. */
  gross_amount_cents?: number | null;
  status: string | null;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  marked_paid_at: string | null;
  marked_paid_by: string | null;
  /** Display only (COL-617): when the payment was paid, for the popup's
   *  "paid on" and its split around an earlier payout. Optional: a caller that
   *  does not select it falls back to `marked_paid_at`. */
  paid_at?: string | null;
  /** The month the rent falls due. Optional: the owners list reads several
   *  months' payments in one query and sorts them into months by this (COL-515);
   *  computeOwnerPayoutGroups itself trusts the caller to pass one month. */
  due_date?: string | null;
  /** COL-417 amount gate (isShortOfExpected) — optional so a caller that does
   *  not select it keeps the gate silent, exactly as isPaymentEffectivelyPaid
   *  treats it. */
  received_cents?: number | null;
}

export interface PayoutProofRow {
  payment_id: string;
  status: string | null;
}

export interface PayoutInvoiceRow {
  /** The invoice's own id. Selected so a payout group can name the invoices
   *  its figures are built from (COL-516); every other reader works off the
   *  totals and never needed it. */
  id?: string | null;
  property_id: number | string | null;
  /** Set only on the pre-20260911 PER-APARTMENT rows: the current generator
   *  always writes NULL here (its unique index is predicated on it). */
  apartment_id?: number | string | null;
  lessor_id: string | null;
  period_month: string | null;
  status: string | null;
  settlement: string | null;
  /** The invoice's fee. On a live offset row this is the collected-based fee
   *  the DB keeps current (COL-474), so the payouts view reads it as-is. */
  amount_cents?: number | null;
  /** Set once the document was emailed — its amount is frozen from then on. */
  sent_at?: string | null;
  /** Set when the payout is marked made (COL-617 reads it as the close time
   *  of an invoice that was paid out without being sent). */
  paid_at?: string | null;
  /** "MF-2026-09-003": names the invoices a payout is still waiting on. */
  invoice_no?: string | null;
  /** COL-483: on a REMAINDER, the number of the invoice it follows (from its
   *  fee_basis); NULL on the month's first invoice. */
  remainder_of_invoice_no?: string | null;
  /**
   * The invoice's own figures from fee_basis (COL-474 / COL-483), selected as
   * scalars and frozen with the rest of its snapshot once it closes: the rent
   * collected for the month, the rent that was already in when the month's
   * previous invoice closed (remainders only), the rent collected above that,
   * and whether the month's ledger still holds a rent row not yet paid.
   */
  collected_rent_eur?: number | string | null;
  prior_collected_rent_eur?: number | string | null;
  collected_since_close_eur?: number | string | null;
  rent_outstanding?: boolean | null;
  /** Set on a cancel-and-reissue replacement (metadata.replaces_invoice_id).
   *  Unsent, but it corrects an invoice that was already sent: its figures
   *  are that invoice's frozen snapshot, and they no longer follow rent. */
  replaces_invoice_id?: string | null;
}

/**
 * Where a payout group stands this month (COL-474), in the order an admin
 * works through them: rent still coming in → collected and waiting to be paid
 * out → paid out.
 */
export type PayoutStatus = "pending" | "ready" | "completed";

/**
 * COL-617: one tenancy's rent for the payout month, as the payouts popup lists
 * it. The same per-tenancy figures the group's totals are summed from, so the
 * popup and the row never disagree.
 */
export interface PayoutTenancyDetail {
  bookingId: string;
  tenantName: string | null;
  expectedEur: number;
  /** Counted up to what the tenancy owes, like the group's `collectedEur`. */
  collectedEur: number;
  shortfallEur: number;
  /** COL-621: the part of `collectedEur` coliviq funds as a kickback (or
   *  referral credit) — counted as collected rent, though the tenant never
   *  paid it. 0 when none. */
  kickbackEur: number;
  /**
   * COL-621: the rent row the payouts popup can mark as a kickback after the
   * fact — the tenancy's only rent row this month, effectively paid in full
   * for what it charges, with no kickback on it yet, while the tenancy still
   * reads short of its lease rent. Exactly the "rent lowered by hand, and
   * nobody said coliviq pays the difference" case. NULL for anything else: a
   * tenant who has not paid what they were charged is a debt, not a kickback.
   */
  kickbackCandidate: {
    paymentId: string;
    amountCents: number;
    /** What `gross_amount_cents` becomes: the row's amount plus the shortfall. */
    grossCents: number;
  } | null;
  /** COL-625: the rent of a moved-out tenancy that never came in and is
   *  written off; `expectedEur` is then what it paid. 0 when none. */
  writtenOffEur: number;
  /** When the latest payment counted here was paid (`paid_at`, else
   *  `marked_paid_at`). NULL when nothing is paid, or when a counted payment
   *  carries no time — unknown, never "before". */
  lastPaidAt: string | null;
}

/** COL-617: one payout line — since COL-625 one tenancy that owes rent this
 *  month in its room, so a room let twice in the month (a move-out and a
 *  move-in) is two lines. Exactly the lines `roomsOwing` counts, and `unpaid`
 *  exactly the ones `roomsUnpaid` counts. */
export interface PayoutRoomDetail {
  /** `room:booking`, unique per line (a room can hold several). */
  lineId: string;
  roomId: string;
  roomTitle: string | null;
  apartmentId: string;
  apartmentLabel: string | null;
  unpaid: boolean;
  /** The line's tenancy (COL-625: always exactly one). */
  tenancies: PayoutTenancyDetail[];
  expectedEur: number;
  collectedEur: number;
  shortfallEur: number;
  /** Its tenancy's `lastPaidAt`. */
  lastPaidAt: string | null;
}

export interface OwnerPayoutGroup {
  propertyId: string;
  /** The month this group's rent and invoices are for, `YYYY-MM`. The current
   *  month for `payoutGroups`; an earlier one for a carried-over payout
   *  (COL-515). */
  periodMonth: string;
  apartmentIds: string[];
  expectedEur: number;
  paidEur: number;
  /** True once EVERY tenancy in the group has 100% of its current-month rent
   *  collected. Not `paidEur >= expectedEur`: those are group totals, and one
   *  tenancy's overpayment must never cover another's shortfall. */
  collected: boolean;
  /** True when this group's current-month offset invoices are all status=paid:
   *  the month's first invoice and every remainder after it (COL-483). */
  alreadyPaidOut: boolean;
  /**
   * True when a settlement invoice can record this group's payout: either at
   * least one unit bills a fee above zero today (the generator would issue
   * one — it drops zero-fee units before insert, `WHERE COALESCE(fee, 0) > 0`),
   * or a live invoice for the month already exists. See the billability block
   * in computeOwnerPayoutGroups.
   */
  billable: boolean;
  /** collected && billable && !alreadyPaidOut — what the stat button counts/filters on. */
  ready: boolean;
  /**
   * completed = every invoice for the month is paid out AND all rent is in
   * (COL-483: an early payout leaves a remainder open, and a payout made while
   * rent was missing is not the end of the month); ready = `ready`; pending =
   * rent is owed this month and not all of it is in. NULL for a group with
   * nothing to act on: no rent owed at all (vacant), or collected but no
   * invoice can ever settle it (see `billable`) — those sit under no payouts
   * chip.
   */
  status: PayoutStatus | null;
  /**
   * COL-483: the rent the outstanding payout is made from. Until the month's
   * first payout that is all the rent collected; after it, the rent collected
   * since (nothing once every invoice is paid out while rent is still
   * missing); once everything is paid out on all of the month's rent, the
   * whole month again.
   */
  payoutRentEur: number;
  /** COL-483: what the month's payouts already paid the owner, while part of
   *  the month is still open (a later invoice waiting for its payout, or rent
   *  still missing); NULL otherwise. */
  paidOutEur: number | null;
  /**
   * COL-516: the ids of the invoices `livePayoutEur` is the payout FOR — this
   * group's own per-property offset rows for the month that are not paid yet,
   * in the order the chain reads them. Named rather than counted, so a reader
   * holding its own copy of the invoice list can tell that the two agree on
   * WHICH invoices are still waiting, not merely on how many.
   *
   * EMPTY when the chain cannot vouch for the list: no per-property chain for
   * the month (a legacy consolidated or per-apartment document is in play), or
   * a row whose id the caller did not select. A reader must treat empty as
   * "unknown", never as "nothing is waiting".
   */
  unpaidInvoiceIds: string[];
  /** COL-483: the open REMAINDER invoice and the invoice it follows, when the
   *  month was closed early and its rent is still being billed. */
  openRemainder: {
    invoiceNo: string | null;
    followsInvoiceNo: string | null;
  } | null;
  /**
   * COL-617: the payout already made for the month, while `paidOutEur` is set:
   * the invoice the open remainder follows, else the month's latest paid
   * invoice. `closedAt` is when it closed (sent, else marked paid) — the line
   * the popup splits paid rooms on. NULL when nothing was paid out yet.
   */
  priorPayout: { invoiceNo: string | null; closedAt: string | null } | null;
  /** COL-617: the lines behind `roomsOwing`, for the payout popup. */
  rooms: PayoutRoomDetail[];
  /**
   * COL-483: the open invoice's own ledger still holds a rent row that is not
   * effectively paid. Closing it now would open another remainder, so the
   * group is pending, not ready, even when every tenancy reads collected.
   */
  ledgerOutstanding: boolean;
  /** Payout lines: tenancies that owe rent this month, one per tenancy in
   *  its room (COL-625). */
  roomsOwing: number;
  /** Of `roomsOwing`, lines whose tenancy is short. */
  roomsUnpaid: number;
  /** Sum of each tenancy's own shortfall — never netted against another
   *  tenancy's overpayment, same rule as `collected`. */
  shortfallEur: number;
  /** This month's rent actually collected: each tenancy's payments counted up
   *  to what it owes (= expectedEur − shortfallEur). An overpayment is not
   *  the owner's rent for the month, so unlike `paidEur` it never shows here
   *  or in the payout. */
  collectedEur: number;
  /**
   * The CoLiviq fee on what has been collected so far: the live offset
   * invoice's amount when one exists (`feeFromInvoice`), otherwise the same
   * per-model estimate the DB would write.
   */
  liveFeeEur: number;
  feeFromInvoice: boolean;
  /** collectedEur − liveFeeEur, floored at 0: what the owner receives if the
   *  payout ran now. */
  livePayoutEur: number;
  /** The offset invoice was emailed before the payout, so its amount is frozen
   *  (GoBD) and no longer follows collected rent. */
  invoiceFrozen: boolean;
}

/**
 * COL-621: the rent row a tenancy's shortfall can be marked a kickback on, or
 * NULL — see PayoutTenancyDetail.kickbackCandidate for the rule.
 */
function kickbackCandidateFor(
  payments: readonly PayoutPaymentRow[],
  paidCents: number,
  expectedCents: number,
  proofOf: (paymentId: string) => { status: string | null } | undefined
): PayoutTenancyDetail["kickbackCandidate"] {
  if (paidCents >= expectedCents) return null;
  const live = payments.filter((p) => p.status !== "canceled");
  if (live.length !== 1) return null;
  const [row] = live;
  const amountCents = row.amount_cents ?? 0;
  if (
    row.gross_amount_cents != null ||
    amountCents <= 0 ||
    !isPaymentEffectivelyPaid(row, proofOf)
  ) {
    return null;
  }
  return {
    paymentId: String(row.id),
    amountCents,
    grossCents: amountCents + (expectedCents - paidCents),
  };
}

/**
 * One unit's fee on the rent collected so far — the TS twin of the per-model
 * rule in 20260925110000_offset_invoice_live_fee.sql, used only while no live
 * invoice exists to read it from:
 *   - pct_of_rent (COL-625, payout_tenancy_rules_offset_live_fee): pct × each
 *     tenancy's collected rent, capped at that tenancy's own monthly rent
 *     (`pctBaseEur`), and NOT at the roll fee: a room let twice in one month
 *     (a move-out and a move-in) is charged the percentage of both tenancies'
 *     rent
 *   - flat_fee_per_room: fee × rooms whose month is fully paid
 *   - full_rent: collected − master rent, floored at 0 (the owner's payout
 *     never exceeds the master rent)
 * `unitMonthlyRevenue` already is that math on (rooms, rent), so it is fed the
 * collected figures instead of the rent roll — and, bar pct_of_rent, capped at
 * the roll-based fee the invoice was generated with, so a duplicate payment row
 * can never bill more than the month's full fee. Rounded to cents per unit, as
 * the DB rounds each line before summing.
 */
function liveUnitFeeCents(
  feeConfig: UnitFeeConfig,
  occupiedRooms: number,
  occupiedRent: number,
  paidRooms: number,
  collectedEur: number,
  pctBaseEur: number
): number {
  const rollFee = unitMonthlyRevenue(feeConfig, occupiedRooms, occupiedRent);
  // A unit whose roll fee is 0 is dropped by the generator
  // (`WHERE COALESCE(fee, 0) > 0`), so no invoice ever bills it, whatever its
  // rent collected — pct_of_rent included, though it no longer caps at the
  // roll fee.
  if (rollFee <= 0) return 0;
  if (feeConfig.revenue_model === "pct_of_rent") {
    return Math.round(
      Math.max(0, unitMonthlyRevenue(feeConfig, 0, pctBaseEur)) * 100
    );
  }
  const liveFee = unitMonthlyRevenue(
    feeConfig,
    Math.min(paidRooms, occupiedRooms),
    collectedEur
  );
  return Math.round(Math.max(0, Math.min(liveFee, rollFee)) * 100);
}

/**
 * Did this tenancy owe rent for the payout month?
 *
 * NOT `isOccupyingNow`. That predicate answers "is this tenant in the room
 * today", which is the right question for a current-tenants list and for the
 * "% rent paid" pill, but the wrong one here: it returns false the moment
 * `actual_move_out_date ?? move_out_date` is in the past, so a tenancy that
 * ENDED EARLIER THIS MONTH drops out of the group's expected AND paid totals
 * at once. It stops being a shortfall instead of counting as one — a tenant
 * who left on the 15th owing the month's rent stopped blocking the payout on
 * the 16th, and the tile told an admin to pay the owner their net rent for a
 * month CoLiviq had not fully collected. Same failure as the cross-tenancy
 * netting bug, reached through occupancy rather than summing.
 *
 * So: overlap with the payout month, not occupancy today. A tenancy counts
 * when it was live for any part of the month — it started on or before the
 * month's last day, and it had not already ended before the month's first.
 * A departure LAST month is excluded, as it should be. And only with a live
 * rent row due in the month (COL-625, `billedThatMonth`): the rent ledger is
 * what was charged.
 *
 * The month's expected amount is then whatever `expectedRentCentsForMonth`
 * says for the tenancy, which pro-rates a move-in but not a move-out: a
 * mid-month departure is expected to have paid the full month. That matches
 * how the rent ledger bills a final month, and it is the safe direction — the
 * error it can make is holding a group back as not-ready, never declaring one
 * ready on rent that never arrived. If the team decides a mid-month departure
 * should be pro-rated, that belongs in `expectedRentCentsForMonth` where every
 * rent surface would pick it up, not in a second rule here.
 */
function owedRentForMonth(
  b: PayoutBookingRow,
  now: Date,
  earlierMonth = false,
  billedThatMonth = false
): boolean {
  // COL-625: no live rent row due in the month, no rent owed for it. The
  // booking's dates alone said a tenancy owed the month, so a tenant who was
  // never charged it (no rent row) read as "nothing paid yet" and held the
  // payout. The offset invoice's own ledger is built from rent rows too.
  if (!billedThatMonth) return false;
  // Archive is read off admin_archived_at (isBookingArchived) and the status
  // off ACTIVE_BOOKING_STATUSES, rather than through isActiveBooking: that
  // helper reads a DIFFERENT archive column on each line (admin_archived_at on
  // main, is_archived on develop), and this module is kept byte-identical
  // across both. These two are the same on either line, and they are what
  // isOccupyingNow itself checks.
  // An archive is read as of today too. For an earlier month (COL-515) a
  // tenancy archived since — finished tenancies get archived — still owed that
  // month if its rent was really billed then: a live rent row due in the month
  // is the evidence. Without one (a duplicate or mistaken booking archived on
  // purpose) it stays out. COL-625: the same holds for a moved-out tenancy in
  // the current month — moved-out tenancies get archived too, and the rent
  // they paid is still the owner's (what they did not pay is written off, see
  // movedOut in computeOwnerPayoutGroups).
  const movedOut = b.status === "moved_out";
  if (
    isBookingArchived({ admin_archived_at: b.admin_archived_at }) &&
    !((earlierMonth || movedOut) && billedThatMonth)
  ) {
    return false;
  }
  // Notice-period tenancies remain in current collection. Earlier-month
  // accounting also retains ended tenancies so a recorded departure never
  // erases a historical payout obligation, and so does the current month for
  // a moved-out one (COL-625): only its rent paid counts, never a shortfall.
  const windingDown = (
    WINDING_DOWN_BOOKING_STATUSES as readonly string[]
  ).includes(b.status ?? "");
  if (
    !RENT_COLLECTION_STATUSES.includes(b.status ?? "") &&
    !((earlierMonth || movedOut) && windingDown)
  ) {
    return false;
  }
  // Calendar dates compared AS calendar dates. `Date.parse("2026-09-01")`
  // is UTC midnight, while `new Date(y, m, 1)` is LOCAL midnight — for an
  // admin west of UTC the local 1st sits hours AFTER the parsed 1st, so a
  // tenancy that ended on the 1st read as ended in the previous month and
  // its unpaid rent stopped blocking. ISO `YYYY-MM-DD` strings sort
  // lexicographically, so the comparison needs no timezone at all; the
  // month bounds come from `now`'s own local calendar, which is the month
  // the tile is labelled with.
  const y = now.getFullYear();
  const m = now.getMonth();
  const mm = String(m + 1).padStart(2, "0");
  const monthStart = `${y}-${mm}-01`;
  const monthEnd = `${y}-${mm}-${String(new Date(y, m + 1, 0).getDate()).padStart(2, "0")}`;

  const startDay = isoDay(effectiveStartDate(b));
  if (startDay !== null && startDay > monthEnd) return false;

  // Same anchor isOccupyingNow uses: only a CONFIRMED departure ends a
  // tenancy. end_date is the earliest POSSIBLE move-out (contracts
  // auto-prolong) and honouring it would drop tenants whose term rolled past.
  const endDay = isoDay(b.actual_move_out_date ?? b.move_out_date);
  // Current notice-period tracking follows status, matching reminders/cards.
  // Historical payout accounting must still respect the billed month's dates.
  const currentNotice = !earlierMonth && isNoticePeriodStatus(b.status);
  if (!currentNotice && endDay !== null && endDay < monthStart) return false;
  if (windingDown && !currentNotice && endDay === null) return false;

  return true;
}

/** Tenancies that are over or ending but still owed rent for the months they
 *  lasted (COL-515). Not in ACTIVE_BOOKING_STATUSES: those answer "who is a
 *  tenant now".
 *
 *  `terminated` and `cancelled` are deliberately NOT here: across the app (the
 *  tenant journey, the activity timeline, the referral rules) they are a
 *  booking that was rejected or never became a tenancy, not a tenancy that ran
 *  and ended — that is `moved_out`. */
const WINDING_DOWN_BOOKING_STATUSES = [
  "notice_given",
  "move_out_pending",
  "moved_out",
] as const;

/** A booking that never became a tenancy (COL-613): pending, cancelled or
 *  archived (by status or `admin_archived_at`), never moved in, and no rent row
 *  with money on it. The DB looks at every rent row of the booking; this sees
 *  only the month's (`rows`), which is all the caller loads. A row settled by
 *  an approved proof (the latest one) counts as money too. `received_cents`
 *  is read on purpose although it is not authoritative: it only ever makes a
 *  booking count, the safe direction. */
function neverATenancy(
  b: PayoutBookingRow,
  rows: PayoutPaymentRow[],
  proofFor: (paymentId: string) => PayoutProofRow | undefined
): boolean {
  const mayBeAbandoned =
    (b.status === "pending" ||
      b.status === "cancelled" ||
      b.status === "archived" ||
      isBookingArchived({ admin_archived_at: b.admin_archived_at })) &&
    !b.actual_move_in_date;
  return (
    mayBeAbandoned &&
    !rows.some(
      (p) =>
        p.status === "paid" ||
        (p.received_cents ?? 0) > 0 ||
        proofFor(p.id)?.status === "approved"
    )
  );
}

/** Whether a booking's rent rows count towards the room reading paid in the
 *  month's rent LEDGER (COL-613): not when it never became a tenancy. SQL
 *  `offset_rent_ledger_counts_row` adds a second condition — another booking
 *  on the room that counts has a live rent row this month — but here it can
 *  never change the result: a never-tenancy row carries no money and is not
 *  settled by a proof, so it is never effectively paid; leaving
 *  it out only turns the room "paid" when some other row on it is paid, and a
 *  paid row means its booking counts and has a row this month. (The SQL needs
 *  the condition for `rent_rooms`, which this view does not compute.)
 *  Change both or neither. Money is judged on the month's rows only (see
 *  neverATenancy), which can only move the fee estimate shown before an
 *  invoice exists: Ready/Pending reads the invoice's own `rent_outstanding`. */
function rentLedgerCountsBooking(
  b: PayoutBookingRow,
  rows: PayoutPaymentRow[],
  proofFor: (paymentId: string) => PayoutProofRow | undefined
): boolean {
  return !neverATenancy(b, rows, proofFor);
}

/** COL-625: a moved-out tenancy's rent that never came in is written off —
 *  reminders and the deposit have recovered what can be. Its paid rent still
 *  counts; what it did not pay neither blocks a payout nor reads as missing.
 *  SQL `offset_invoice_live_fee` drops the same unpaid rows from its ledger;
 *  change both or neither. */
function rentWrittenOff(b: PayoutBookingRow): boolean {
  return b.status === "moved_out";
}

/** The `YYYY-MM-DD` part of a date or ISO timestamp string; null when absent
 *  or not date-shaped, so an unparseable value never silently excludes a
 *  tenancy (it falls through to "counts", the safe direction). */
function isoDay(value: string | null | undefined): string | null {
  if (!value) return null;
  const day = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

/** A fee_basis figure selected as a scalar (a number, or a numeric string),
 *  in cents; NULL when absent or unreadable. */
function eurToCents(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/**
 * The rent a closed invoice settles, off its frozen fee_basis snapshot. A
 * snapshot older than COL-483 has no collected_since_close_eur; it was the
 * month's first invoice, so its slice is all it collected. Shared with the
 * owner profile's Net payout column (owner-invoice-net-payout.ts), so the two
 * surfaces read one definition of what a closed invoice paid out.
 */
export function closedInvoiceSliceCents(
  inv: Pick<
    PayoutInvoiceRow,
    | "collected_since_close_eur"
    | "collected_rent_eur"
    | "prior_collected_rent_eur"
  >
): number {
  return (
    eurToCents(inv.collected_since_close_eur) ??
    Math.max(
      (eurToCents(inv.collected_rent_eur) ?? 0) -
        (eurToCents(inv.prior_collected_rent_eur) ?? 0),
      0
    )
  );
}

/**
 * One group's offset invoices for the month, reduced to what its payout row
 * shows (COL-483).
 *
 * Each invoice of the month settles its OWN slice of the rent: the month's
 * first invoice the rent that was in when it closed, each remainder the rent
 * collected after the invoice before it closed. So once part of the month is
 * paid out, the payout still to make is the unpaid invoices' slices less their
 * fees, never the whole month's rent again (which would pay the first slice
 * out twice).
 *   - Nothing paid out yet, or every payout made on all of the month's rent:
 *     the month as a whole — its collected rent (the view's own, capped per
 *     tenancy) less every invoice's fee, exactly as before remainders existed.
 *   - A closed (sent, or reissued) invoice still waiting for its payout: the
 *     rent its frozen snapshot recorded.
 *   - The open remainder: the rent collected since the previous close — the
 *     view's collected rent less what was in at that close, and never more than
 *     the DB's own figure for it, so a duplicate payment cannot inflate it.
 *   - Every invoice paid out while rent is still missing (`allRentIn` false):
 *     nothing more to pay out yet. Never the month's whole payout again,
 *     which was already made.
 * What was already paid out is read off the view's own collected rent, less
 * what the unpaid invoices still settle and the fees the paid ones billed —
 * not off the paid invoices' snapshots, which cover only the units an invoice
 * bills: a unit billed nothing (a full_rent unit at its master rent) is paid
 * out all the same. Rent that arrived after the month's last payout with no
 * invoice left to bill it reads as paid out too (the known gap in
 * 20260925140100_offset_remainder_invoices.sql). And never less than the paid
 * invoices' own slices less their fees, so rent returned after a payout does
 * not read as the payout undone.
 * No invoice yet: the estimate the caller passes.
 */
function offsetChainFigures(
  invoices: PayoutInvoiceRow[],
  collectedCents: number,
  estimatedFeeCents: number,
  allRentIn: boolean
): {
  fromInvoice: boolean;
  feeCents: number;
  payoutRentCents: number;
  payoutCents: number;
  paidOutCents: number | null;
  unpaidInvoiceIds: string[];
  openRemainder: OwnerPayoutGroup["openRemainder"];
  priorPayout: OwnerPayoutGroup["priorPayout"];
  ledgerOutstanding: boolean;
  frozen: boolean;
} {
  if (invoices.length === 0) {
    return {
      fromInvoice: false,
      feeCents: estimatedFeeCents,
      payoutRentCents: collectedCents,
      payoutCents: Math.max(collectedCents - estimatedFeeCents, 0),
      paidOutCents: null,
      unpaidInvoiceIds: [],
      openRemainder: null,
      priorPayout: null,
      ledgerOutstanding: false,
      frozen: false,
    };
  }
  const sum = (xs: number[]) => xs.reduce((s, x) => s + x, 0);
  const fee = (inv: PayoutInvoiceRow) => inv.amount_cents ?? 0;
  const unpaid = invoices.filter((inv) => inv.status !== "paid");
  const paid = invoices.filter((inv) => inv.status === "paid");
  // At most one invoice of a month is open (unsent, unpaid, and following the
  // rent) at a time. A reissue replacement is unsent too, but stands in for an
  // invoice that was sent, so it waits for its payout like one.
  const isReissue = (inv: PayoutInvoiceRow) => !!inv.replaces_invoice_id;
  const open = unpaid.find((inv) => !inv.sent_at && !isReissue(inv)) ?? null;
  const ledgerOutstanding = open?.rent_outstanding === true;
  const openRemainder = open?.remainder_of_invoice_no
    ? {
        invoiceNo: open.invoice_no ?? null,
        followsInvoiceNo: open.remainder_of_invoice_no,
      }
    : null;
  // Nothing follows collected rent any more, and a sent invoice still waits
  // for its payout: its amount (and so the payout) is fixed.
  const frozen = !open && unpaid.some((inv) => !!inv.sent_at || isReissue(inv));

  // The invoices this chain's payout figures are FOR — the ones still waiting
  // to be paid out — named by id (COL-516). Empty unless every one of them
  // carries an id: a partly named set would let a reader believe the two sides
  // mean the same invoices when one of them is unaccounted for.
  const unpaidIds = unpaid
    .map((inv) => inv.id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
  const unpaidInvoiceIds = unpaidIds.length === unpaid.length ? unpaidIds : [];

  const monthTotals = () => {
    const feeCents = sum(invoices.map(fee));
    return {
      fromInvoice: true,
      feeCents,
      payoutRentCents: collectedCents,
      payoutCents: Math.max(collectedCents - feeCents, 0),
      paidOutCents: null,
      unpaidInvoiceIds,
      openRemainder,
      priorPayout: null,
      ledgerOutstanding,
      frozen,
    };
  };
  if (paid.length === 0) return monthTotals();
  // Every payout of the month made, on all of its rent: the month as a record.
  if (unpaid.length === 0 && allRentIn) return monthTotals();

  const slice = (inv: PayoutInvoiceRow): number => {
    if (inv !== open) return closedInvoiceSliceCents(inv);
    const since = Math.max(
      collectedCents - (eurToCents(inv.prior_collected_rent_eur) ?? 0),
      0
    );
    const dbSince = eurToCents(inv.collected_since_close_eur);
    return dbSince === null ? since : Math.min(since, dbSince);
  };
  const payoutRentCents = sum(unpaid.map(slice));
  const feeCents = sum(unpaid.map(fee));
  // The payout already made (COL-617): the invoice the open remainder follows,
  // else the latest one paid out.
  const closedAt = (inv: PayoutInvoiceRow) =>
    inv.sent_at ?? inv.paid_at ?? null;
  const followed = openRemainder?.followsInvoiceNo
    ? (invoices.find(
        (inv) => inv.invoice_no === openRemainder.followsInvoiceNo
      ) ?? null)
    : null;
  const latestPaid =
    [...paid].sort((a, b) =>
      (closedAt(b) ?? "").localeCompare(closedAt(a) ?? "")
    )[0] ?? null;
  const prior = followed ?? latestPaid;
  const priorPayout = prior
    ? { invoiceNo: prior.invoice_no ?? null, closedAt: closedAt(prior) }
    : null;
  return {
    fromInvoice: true,
    feeCents,
    payoutRentCents,
    payoutCents: Math.max(payoutRentCents - feeCents, 0),
    // Never below what the paid invoices themselves settled: rent returned
    // after a payout (a SEPA return) does not undo a payout that was made.
    paidOutCents: Math.max(
      collectedCents - payoutRentCents - sum(paid.map(fee)),
      sum(
        paid.map((inv) => Math.max(closedInvoiceSliceCents(inv) - fee(inv), 0))
      ),
      0
    ),
    unpaidInvoiceIds,
    openRemainder,
    priorPayout,
    ledgerOutstanding,
    frozen,
  };
}

/** A date inside `YYYY-MM` (its 1st, local), or null when `month` is absent or
 *  not month-shaped. */
function dateInMonth(month: string | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})$/.exec(month ?? "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, 1) : null;
}

/**
 * The earlier months in which this owner still has an unpaid offset invoice,
 * and the properties it is for (COL-515): `YYYY-MM` -> property ids. Only a
 * live per-property offset document counts, the same row `computeOwnerPayoutGroups`
 * builds a group's chain from — not a canceled one, a paid one, a legacy
 * per-apartment or portfolio-wide row, or a `transfer` invoice (that is the
 * owner's own fee bill, not a payout). A month that appears here has a payout
 * still to settle; one that does not never produces a carried-over row, so a
 * group that merely EXISTS for an old month (an apartment that has since moved
 * to another owner, a month before offset invoicing) is never offered.
 */
export function openOffsetPropertiesByMonth(
  invoices: PayoutInvoiceRow[],
  lessorId: string,
  currentMonth: string
): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const inv of invoices) {
    if (inv.lessor_id !== lessorId) continue;
    const month = String(inv.period_month ?? "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(month) || month >= currentMonth) continue;
    if (inv.status === "canceled" || inv.status === "paid") continue;
    if (inv.property_id == null || inv.apartment_id != null) continue;
    if (inv.settlement !== "offset") continue;
    const properties = out.get(month) ?? new Set<string>();
    properties.add(String(inv.property_id));
    out.set(month, properties);
  }
  return out;
}

/**
 * Group one owner's apartments by property (company_collects only) and decide
 * payout readiness for each group. Pure and side-effect-free so the grouping/
 * readiness rule can be unit tested without a database.
 */
export function computeOwnerPayoutGroups(args: {
  lessorId: string;
  apartments: PayoutApartmentInput[];
  rooms: PayoutRoomRow[];
  bookings: PayoutBookingRow[];
  payments: PayoutPaymentRow[];
  proofs: PayoutProofRow[];
  /** This lessor's management_invoices rows, any property/period/settlement. */
  invoices: PayoutInvoiceRow[];
  /**
   * `YYYY-MM`: compute the groups for this EARLIER month instead of `now`'s
   * (COL-515). Every month-scoped rule below — which tenancies owed rent, what
   * each owed, which invoices belong — reads the calendar month off `now`, so
   * an earlier month is run by pointing `now` into it. The caller passes only
   * that month's rent payments; the invoices are filtered here.
   *
   * The tenancies, rooms and rents are today's, not a snapshot of that month:
   * a tenancy that wound down since still counts (see
   * WINDING_DOWN_BOOKING_STATUSES), and so does one archived since if the
   * month's ledger holds a live rent row for it, but a rent changed since is
   * expected at its new amount and an apartment that has changed owner is
   * grouped under its current one (so its old owner's open invoice finds no
   * group). A group left with no rent owed reads as nothing to act on (status
   * null).
   */
  month?: string;
  now?: Date;
}): OwnerPayoutGroup[] {
  const { lessorId, apartments, rooms, bookings, payments, proofs, invoices } =
    args;
  const monthDate = dateInMonth(args.month);
  const earlierMonth = monthDate !== null;
  const now = monthDate ?? args.now ?? new Date();

  const payoutApartments = apartments.filter(
    (a) => a.collectionMode === "company_collects"
  );
  if (payoutApartments.length === 0) return [];

  const apartmentsByProperty = new Map<string, PayoutApartmentInput[]>();
  for (const a of payoutApartments) {
    const key = String(a.propertyId);
    const arr = apartmentsByProperty.get(key) ?? [];
    arr.push(a);
    apartmentsByProperty.set(key, arr);
  }

  const roomsByApartment = new Map<string, PayoutRoomRow[]>();
  for (const r of rooms) {
    if (r.apartmentId == null) continue;
    const key = String(r.apartmentId);
    const arr = roomsByApartment.get(key) ?? [];
    arr.push(r);
    roomsByApartment.set(key, arr);
  }

  const bookingsByRoom = new Map<string, PayoutBookingRow[]>();
  for (const b of bookings) {
    if (b.roomId == null) continue;
    const key = String(b.roomId);
    const arr = bookingsByRoom.get(key) ?? [];
    arr.push(b);
    bookingsByRoom.set(key, arr);
  }

  const proofByPayment = latestProofByPaymentId(proofs);
  const paymentsByBooking = new Map<string, PayoutPaymentRow[]>();
  for (const p of payments) {
    if (p.bookingId == null) continue;
    const key = String(p.bookingId);
    const arr = paymentsByBooking.get(key) ?? [];
    arr.push(p);
    paymentsByBooking.set(key, arr);
  }

  const ymKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  // property -> "has a live offset row for this month, and every one of them
  // is paid". Steady-state (post 20260911120000) there is one live
  // (non-canceled) offset row per (property, lessor, month) until the month is
  // closed early, and then one more per remainder (COL-483: the unique index
  // is per remainder_seq). Every one of them has to be paid: an early payout
  // leaves the remainder billing the rest of the month. That migration's own
  // generator guard also SKIPS re-billing a
  // property while an un-canceled LEGACY PER-APARTMENT offset row (from
  // before the per-property consolidation) still exists for the group's own
  // apartments, so a property can carry several such rows for the SAME month
  // during the transition. Requiring ALL of them paid (not just one) avoids
  // reading a property as already paid out while a sibling apartment's fee
  // is still outstanding.
  const offsetInvoiceStateByProperty = new Map<
    string,
    { sawRow: boolean; allPaid: boolean }
  >();
  // The group's OWN per-property offset rows: the month's first invoice and
  // any remainder after it (COL-483), each carrying the fee the DB keeps in
  // step with collected rent (COL-474) until it closes, and in every case the
  // fees that will actually be netted. Per-apartment and consolidated legacy
  // rows are left out: they bill a different slice than the group, so the
  // view estimates instead.
  const offsetChainByProperty = new Map<string, PayoutInvoiceRow[]>();
  // Where a PER-APARTMENT invoice lands. The generator stopped setting
  // apartment_id at 20260911120000, so a row that carries one is necessarily a
  // legacy document — and its guard skips a group only while such a row exists
  // for one of THAT GROUP'S OWN apartment ids, which is why routing by
  // apartment (not by property) is what mirrors it.
  const propertyByPayoutApartment = new Map<string, string>();
  for (const a of payoutApartments) {
    propertyByPayoutApartment.set(String(a.id), String(a.propertyId));
  }
  let consolidatedState: { sawRow: boolean; allPaid: boolean } | null = null;
  for (const inv of invoices) {
    if (inv.lessor_id !== lessorId) continue;
    if (String(inv.period_month ?? "").slice(0, 7) !== ymKey) continue;
    // A canceled row was voided (Storno) — it is not live work and must not
    // count against "all paid", the same way it is excluded from the DB's
    // own uniqueness predicate.
    if (inv.status === "canceled") continue;

    if (inv.property_id == null) {
      const state = consolidatedState ?? { sawRow: false, allPaid: true };
      state.sawRow = true;
      if (inv.status !== "paid") state.allPaid = false;
      consolidatedState = state;
      continue;
    }

    // A LEGACY PER-APARTMENT row (apartment_id set — the current generator
    // never writes one). Its settlement may be NULL, from before
    // 20260908140000 added the column, so it cannot be matched on 'offset';
    // what matters is that while it is live the generator refuses to bill the
    // group it belongs to, leaving no offset row to settle. Routed to that
    // apartment's OWN group: a row for an apartment outside every payout group
    // (an owner_collects sibling in the same building) blocks nothing here and
    // must not clear somebody else's payout.
    const aptKey = inv.apartment_id == null ? null : String(inv.apartment_id);
    let key: string;
    if (aptKey != null) {
      const owningProperty = propertyByPayoutApartment.get(aptKey);
      if (owningProperty == null) continue;
      key = owningProperty;
    } else {
      // A per-property row. Only settlement='offset' records an executed
      // payout: a 'transfer' row is the owner's own fee invoice, billed
      // alongside this group's offset row under the same (property, lessor)
      // pair — settlement is part of the grouping key precisely so the two
      // stay separate documents. A legacy per-property row with a NULL or
      // 'transfer' settlement DOES block billing the same way the shapes
      // above do, but it is indistinguishable here from that live transfer
      // row (telling them apart needs fee_basis ? 'properties', the
      // generator's own test, which the owners-list query does not carry), so
      // it is deliberately left out rather than risk clearing a payout on an
      // invoice that transferred no rent.
      if (inv.settlement !== "offset") continue;
      key = String(inv.property_id);
      const chain = offsetChainByProperty.get(key) ?? [];
      chain.push(inv);
      offsetChainByProperty.set(key, chain);
    }

    const state = offsetInvoiceStateByProperty.get(key) ?? {
      sawRow: false,
      allPaid: true,
    };
    state.sawRow = true;
    if (inv.status !== "paid") state.allPaid = false;
    offsetInvoiceStateByProperty.set(key, state);
  }

  const groups: OwnerPayoutGroup[] = [];
  for (const [propertyId, apts] of apartmentsByProperty) {
    // Accumulate in integer cents, not EUR floats: "collected" must be an
    // exact comparison — a genuine one-cent shortfall must never read as
    // fully paid, which a float tolerance could mask.
    let expectedCents = 0;
    let paidCents = 0;
    // Readiness is decided PER TENANCY, not on the group's totals. Summing
    // first and comparing once let one tenancy's overpayment silently cover
    // another's shortfall: two apartments each expecting EUR 1,000, one with a
    // duplicate/corrected payment row worth EUR 2,000 and the other with
    // nothing paid at all, satisfied `paidCents >= expectedCents` and read as
    // ready — a payout executed on rent that was never collected. The group
    // totals below stay as they were (they are the figures an admin reads);
    // only the decision moves down a level.
    let everyTenancyCollected = true;
    // Can an offset invoice EXIST for this group? The generator drops
    // zero-fee units before it inserts anything (`WHERE COALESCE(fee, 0) > 0`)
    // and groups what survives, so a group whose every unit bills nothing
    // never produces a document — and `alreadyPaidOut`, which reads that
    // document, could never become true. Without this the group would read
    // ready indefinitely once its rent was collected, with no action able to
    // settle it: an admin filters to it, opens the owner and finds nothing to
    // do. That is the same dead end 20260924120200 closed for the retired
    // offset switch, reached by the zero-fee route instead.
    //
    // Fee inputs mirror the generator: occupied ROOM COUNT and the sum of
    // those rooms' `price` (rent_base), not the discounted per-tenancy
    // expectation above — the fee is charged on the rent roll, and a unit is
    // occupied here when it holds a tenancy the group is already counting.
    let billable = false;
    let roomsOwing = 0;
    let roomsUnpaid = 0;
    let shortfallCents = 0;
    let collectedCents = 0;
    let estimatedFeeCents = 0;
    const roomDetails: PayoutRoomDetail[] = [];
    for (const apt of apts) {
      const aptRooms = roomsByApartment.get(String(apt.id)) ?? [];
      let unitOccupiedRooms = 0;
      let unitOccupiedRent = 0;
      // The live fee reads the month's rent LEDGER per room, exactly as the DB
      // does: every rent row due this month on the room's bookings (bar those
      // that never became a tenancy, rentLedgerCountsBooking, and a moved-out
      // tenancy's unpaid rent, rentWrittenOff), collected when effectively
      // paid, the room "paid" when it has rows and all are.
      let unitCollectedCents = 0;
      let unitPaidRooms = 0;
      // COL-625: collected rent capped per tenancy, the pct_of_rent base.
      let unitPctBaseCents = 0;
      for (const room of aptRooms) {
        // Billability mirrors the generator's own occupancy test verbatim
        // (generate_management_invoices: rm.status = 'occupied' OR
        // rm.available = false) — not the booking-date signal below, which
        // decides WHAT'S EXPECTED/PAID, not WHETHER a fee can be billed at
        // all. The two can disagree (e.g. a room flagged unavailable ahead of
        // a future-dated booking), and using the booking-only signal here
        // could show a unit as unbillable that the generator will bill.
        if (room.status === "occupied" || room.available === false) {
          unitOccupiedRooms += 1;
          unitOccupiedRent += room.price ?? 0;
        }
        const roomBookings = bookingsByRoom.get(String(room.id)) ?? [];
        // Every tenancy that owed rent for THIS MONTH, including one that
        // ended mid-month — see owedRentForMonth for why occupancy today is
        // the wrong test for a "was the rent collected" question.
        const active = roomBookings.filter((b) =>
          owedRentForMonth(
            b,
            now,
            earlierMonth,
            (paymentsByBooking.get(String(b.id)) ?? []).some(
              (p) => p.status !== "canceled"
            )
          )
        );
        let ledgerRows = 0;
        let ledgerAllPaid = true;
        for (const b of roomBookings) {
          const bookingRows = paymentsByBooking.get(String(b.id)) ?? [];
          if (
            !rentLedgerCountsBooking(b, bookingRows, (id) =>
              proofByPayment.get(id)
            )
          )
            continue;
          let tenancyLedgerPaidCents = 0;
          for (const p of bookingRows) {
            // A voided rent row is not part of the month's ledger (the DB
            // excludes it the same way).
            if (p.status === "canceled") continue;
            const proofFor = (id: string) => proofByPayment.get(id);
            const paid = isPaymentEffectivelyPaid(p, proofFor);
            // COL-625: a moved-out tenancy's unpaid rent is written off. A row
            // not paid in full keeps what was received on it and counts as
            // settled; one with nothing received leaves the ledger (the DB's
            // rule too).
            const writtenOff = !paid && rentWrittenOff(b);
            const rowCents = paid
              ? ownerRentCents(p)
              : writtenOff
                ? resolveRentSettlement(p, proofFor).appliedCents
                : 0;
            if (writtenOff && rowCents <= 0) continue;
            ledgerRows += 1;
            if (paid || writtenOff) {
              unitCollectedCents += rowCents;
              tenancyLedgerPaidCents += rowCents;
            } else {
              ledgerAllPaid = false;
            }
          }
          // COL-625: the pct_of_rent base, tenancy by tenancy, each capped at
          // its own monthly rent (see liveUnitFeeCents).
          const tenancyRentCents = Math.round(
            getFullMonthlyRentFromBooking({
              monthly_rent: b.monthly_rent,
              rooms: { price: room.price },
            }) * 100
          );
          unitPctBaseCents +=
            tenancyRentCents > 0
              ? Math.min(tenancyLedgerPaidCents, tenancyRentCents)
              : tenancyLedgerPaidCents;
        }
        if (ledgerRows > 0 && ledgerAllPaid) unitPaidRooms += 1;
        // COL-625: one payout line per tenancy. A room let twice in the month
        // (one tenant moving out, the next moving in) is two lines, each
        // paid or unpaid on its own.
        for (const b of active) {
          const fullMonthlyRent = getFullMonthlyRentFromBooking({
            monthly_rent: b.monthly_rent,
            rooms: { price: room.price },
          });
          let bookingExpectedCents = expectedRentCentsForMonth({
            fullMonthlyRent,
            moveInIso: effectiveStartDate(b),
            firstMonthOverride: b.first_month_rent_override,
            dueYear: now.getFullYear(),
            dueMonthIndex0: now.getMonth(),
            discount: {
              rent_discount_mode: b.rent_discount_mode,
              rent_discount_value: b.rent_discount_value,
              rent_discount_months: b.rent_discount_months,
              rent_discount_start_month: b.rent_discount_start_month,
            },
          });

          let bookingPaidCents = 0;
          let bookingKickbackCents = 0;
          // Latest paid time of the counted payments; undefined once one of
          // them has none (unknown).
          let lastPaidAt: string | null | undefined = null;
          const bookingPayments = paymentsByBooking.get(String(b.id)) ?? [];
          for (const p of bookingPayments) {
            if (p.status === "canceled" || p.status === "failed") continue;
            const settlement = resolveRentSettlement(p, (id) =>
              proofByPayment.get(id)
            );
            // A settled tenant charge includes the company-funded rent
            // differential. Partial receipts count only money received.
            const collected = settlement.isSettled
              ? ownerRentCents(p)
              : settlement.appliedCents;
            if (collected <= 0) continue;
            bookingPaidCents += collected;
            if (settlement.isSettled) bookingKickbackCents += kickbackCents(p);
            const at = p.paid_at ?? p.marked_paid_at ?? null;
            if (at === null) lastPaidAt = undefined;
            else if (lastPaidAt !== undefined && (lastPaidAt ?? "") < at)
              lastPaidAt = at;
          }
          paidCents += bookingPaidCents;
          // COL-625: once moved out, what the tenancy did not pay is written
          // off. It owes what it paid: never short, never blocking, and with
          // nothing paid it leaves the payout altogether.
          let writtenOffCents = 0;
          if (rentWrittenOff(b) && bookingPaidCents < bookingExpectedCents) {
            writtenOffCents = bookingExpectedCents - bookingPaidCents;
            bookingExpectedCents = bookingPaidCents;
          }
          expectedCents += bookingExpectedCents;

          // A tenancy billed nothing this month (moved out mid-month, a
          // 100% discount) is not a shortfall and must not block the group.
          if (bookingExpectedCents <= 0) continue;
          const tenancyCollected = Math.min(
            bookingPaidCents,
            bookingExpectedCents
          );
          const short = bookingPaidCents < bookingExpectedCents;
          const tenancy: PayoutTenancyDetail = {
            bookingId: String(b.id),
            tenantName: b.name ?? null,
            expectedEur: bookingExpectedCents / 100,
            collectedEur: tenancyCollected / 100,
            shortfallEur: (bookingExpectedCents - tenancyCollected) / 100,
            kickbackEur: Math.min(bookingKickbackCents, tenancyCollected) / 100,
            kickbackCandidate: kickbackCandidateFor(
              bookingPayments,
              bookingPaidCents,
              bookingExpectedCents,
              (id) => proofByPayment.get(id)
            ),
            writtenOffEur: writtenOffCents / 100,
            lastPaidAt: lastPaidAt ?? null,
          };
          collectedCents += tenancyCollected;
          roomsOwing += 1;
          if (short) {
            everyTenancyCollected = false;
            roomsUnpaid += 1;
            shortfallCents += bookingExpectedCents - bookingPaidCents;
          }
          roomDetails.push({
            lineId: `${room.id}:${b.id}`,
            roomId: String(room.id),
            roomTitle: room.title ?? null,
            apartmentId: String(apt.id),
            apartmentLabel: apt.label ?? null,
            unpaid: short,
            tenancies: [tenancy],
            expectedEur: tenancy.expectedEur,
            collectedEur: tenancy.collectedEur,
            shortfallEur: tenancy.shortfallEur,
            lastPaidAt: tenancy.lastPaidAt,
          });
        }
      }
      if (
        unitMonthlyRevenue(apt.feeConfig, unitOccupiedRooms, unitOccupiedRent) >
        0
      ) {
        billable = true;
      }
      estimatedFeeCents += liveUnitFeeCents(
        apt.feeConfig,
        unitOccupiedRooms,
        unitOccupiedRent,
        unitPaidRooms,
        unitCollectedCents / 100,
        unitPctBaseCents / 100
      );
    }

    const collected = expectedCents > 0 && everyTenancyCollected;
    const propertyState = offsetInvoiceStateByProperty.get(propertyId);
    // A live portfolio-wide consolidated row covers this group too, and both
    // it and the group's own row must be paid before the payout is recorded.
    const sawRow = !!propertyState?.sawRow || !!consolidatedState?.sawRow;
    const allPaid =
      (propertyState?.allPaid ?? true) && (consolidatedState?.allPaid ?? true);
    const alreadyPaidOut = sawRow && allPaid;
    // `billable` asks "could an invoice be GENERATED for this group today".
    // When one already EXISTS for the month, that question is moot — the
    // document that records the payout is right there, waiting to be marked
    // paid. Without this, a group whose last tenant paid up and left (room
    // now `available`), or whose fee config was zeroed after the run, read
    // unbillable and vanished from the ready filter with a live pending
    // invoice an admin could — and should — settle.
    const settleable = billable || sawRow;
    // No legacy document in play → the group's own rows are the whole fee.
    const chain = offsetChainFigures(
      consolidatedState?.sawRow
        ? []
        : (offsetChainByProperty.get(propertyId) ?? []),
      collectedCents,
      estimatedFeeCents,
      everyTenancyCollected
    );
    // Ready also needs the open invoice's own ledger clear (COL-483): paying
    // it out with a rent row still open would open another remainder rather
    // than settle the month — the DB's rule, read off the DB's own snapshot.
    const ready =
      collected && !chain.ledgerOutstanding && settleable && !alreadyPaidOut;
    // Completed only once every invoice for the month is paid AND all rent is
    // in (COL-483): a payout made while rent was missing leaves the group
    // pending. everyTenancyCollected rather than `collected`, so a month with
    // nothing owed that was paid out still reads completed.
    const status: PayoutStatus | null =
      alreadyPaidOut && everyTenancyCollected
        ? "completed"
        : ready
          ? "ready"
          : expectedCents > 0 &&
              (!collected || chain.ledgerOutstanding || alreadyPaidOut)
            ? "pending"
            : null;
    // COL-621: a kickback raises the rent the payout counts, and only a live,
    // unsent invoice follows that (its live fee, or the remainder it opened).
    // Once the month is paid out or its invoice was sent, marking one would
    // read as collected with no document ever paying it out, so none is offered.
    if (alreadyPaidOut || chain.frozen) {
      for (const r of roomDetails)
        for (const t of r.tenancies) t.kickbackCandidate = null;
    }
    groups.push({
      propertyId,
      periodMonth: ymKey,
      apartmentIds: apts.map((a) => String(a.id)),
      expectedEur: expectedCents / 100,
      paidEur: paidCents / 100,
      collected,
      alreadyPaidOut,
      billable: settleable,
      ready,
      status,
      roomsOwing,
      roomsUnpaid,
      shortfallEur: shortfallCents / 100,
      collectedEur: collectedCents / 100,
      liveFeeEur: chain.feeCents / 100,
      feeFromInvoice: chain.fromInvoice,
      livePayoutEur: chain.payoutCents / 100,
      invoiceFrozen: chain.frozen && !alreadyPaidOut,
      payoutRentEur: chain.payoutRentCents / 100,
      paidOutEur: chain.paidOutCents === null ? null : chain.paidOutCents / 100,
      unpaidInvoiceIds: chain.unpaidInvoiceIds,
      openRemainder: chain.openRemainder,
      priorPayout: chain.paidOutCents === null ? null : chain.priorPayout,
      rooms: roomDetails,
      ledgerOutstanding: chain.ledgerOutstanding,
    });
  }
  return groups;
}

/**
 * COL-617: a payout's rooms as its popup lists them — unpaid first, then the
 * paid ones. Once part of the month was paid out (`priorPayout`), the paid
 * rooms split on when that payout closed: a room whose rent was all in by then
 * went out with it; any other paid room — including one whose paid time is
 * unknown — counts toward the payout still to make. Without a close time to
 * compare against there is no split: `paidBefore` is null.
 */
export function payoutRoomSections(
  group: Pick<OwnerPayoutGroup, "rooms" | "priorPayout">
): {
  unpaid: PayoutRoomDetail[];
  paid: PayoutRoomDetail[];
  paidBefore: PayoutRoomDetail[] | null;
} {
  const byName = (a: PayoutRoomDetail, b: PayoutRoomDetail) =>
    (a.apartmentLabel ?? "").localeCompare(b.apartmentLabel ?? "", undefined, {
      numeric: true,
    }) ||
    (a.roomTitle ?? "").localeCompare(b.roomTitle ?? "", undefined, {
      numeric: true,
    });
  const rooms = [...group.rooms].sort(byName);
  const unpaid = rooms.filter((r) => r.unpaid);
  const paidRooms = rooms.filter((r) => !r.unpaid);
  const closedAt = group.priorPayout?.closedAt ?? null;
  if (closedAt === null) return { unpaid, paid: paidRooms, paidBefore: null };
  const before = (r: PayoutRoomDetail) =>
    r.lastPaidAt !== null && Date.parse(r.lastPaidAt) <= Date.parse(closedAt);
  return {
    unpaid,
    paid: paidRooms.filter((r) => !before(r)),
    paidBefore: paidRooms.filter(before),
  };
}

/** Number of ready payout groups — what the stat tile counts. */
export function countReadyPayoutGroups(groups: OwnerPayoutGroup[]): number {
  return groups.filter((g) => g.ready).length;
}

/** Rent payments sorted into the months they fall due, `YYYY-MM` -> rows. */
export function groupPaymentsByDueMonth(
  payments: PayoutPaymentRow[]
): Map<string, PayoutPaymentRow[]> {
  const out = new Map<string, PayoutPaymentRow[]>();
  for (const p of payments) {
    const month = String(p.due_date ?? "").slice(0, 7);
    const arr = out.get(month) ?? [];
    arr.push(p);
    out.set(month, arr);
  }
  return out;
}

/**
 * One owner's payouts still open from earlier months (COL-515), oldest month
 * first: each month in `openMonths` is computed the way the current month is,
 * from that month's own rent payments, and only the properties its unpaid
 * offset invoice is for are kept — a group that merely exists for an old month
 * (an apartment that has since changed owner) has no invoice of this owner to
 * settle. Completed groups are not carried over; nothing is owed on them.
 */
export function computeCarriedOverPayoutGroups(args: {
  lessorId: string;
  apartments: PayoutApartmentInput[];
  rooms: PayoutRoomRow[];
  bookings: PayoutBookingRow[];
  proofs: PayoutProofRow[];
  /** This owner's invoices of any period. */
  invoices: PayoutInvoiceRow[];
  /** Earlier rent payments, by the `YYYY-MM` they fall due. */
  paymentsByMonth: Map<string, PayoutPaymentRow[]>;
  /** From openOffsetPropertiesByMonth: month -> properties with an unpaid
   *  offset invoice. */
  openMonths: Map<string, Set<string>>;
}): OwnerPayoutGroup[] {
  const { paymentsByMonth, openMonths, ...shared } = args;
  const out: OwnerPayoutGroup[] = [];
  for (const month of [...openMonths.keys()].sort()) {
    const invoiceProperties = openMonths.get(month);
    for (const group of computeOwnerPayoutGroups({
      ...shared,
      payments: paymentsByMonth.get(month) ?? [],
      month,
    })) {
      if (
        invoiceProperties?.has(group.propertyId) &&
        (group.status === "pending" || group.status === "ready")
      ) {
        out.push(group);
      }
    }
  }
  return out;
}
